export const GAME_TIME_ZONE = "Europe/Madrid";

const dateFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: GAME_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const partsFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: GAME_TIME_ZONE,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

interface DateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function partsInMadrid(date: Date): DateParts {
  const parts = partsFmt.formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  // "24" aparece a medianoche en algunas implementaciones; normalízalo a 0.
  const hour = get("hour") % 24;
  return { year: get("year"), month: get("month"), day: get("day"), hour, minute: get("minute"), second: get("second") };
}

/**
 * Día de juego (YYYY-MM-DD) en Europe/Madrid. Es el mismo para todos los
 * jugadores y cambia a las 00:00 de Madrid, tenga o no horario de verano.
 */
export function gameDate(now: Date = new Date()): string {
  return dateFmt.format(now);
}

/** Desplazamiento de la zona (ms) tal que hora_local = hora_utc + offset. */
function zoneOffsetMs(date: Date): number {
  const p = partsInMadrid(date);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - date.getTime();
}

/** Instante UTC de la próxima medianoche en Europe/Madrid. */
export function nextRolloverAt(now: Date = new Date()): Date {
  const p = partsInMadrid(now);
  const nextMidnightUtc = Date.UTC(p.year, p.month - 1, p.day + 1, 0, 0, 0);
  // Dos pasadas para converger en los cambios de horario de verano.
  let guess = nextMidnightUtc;
  for (let i = 0; i < 2; i += 1) {
    guess = nextMidnightUtc - zoneOffsetMs(new Date(guess));
  }
  return new Date(guess);
}

/** Suma (o resta) días naturales a una fecha YYYY-MM-DD. */
export function addDays(gameDateStr: string, days: number): string {
  const [y, m, d] = gameDateStr.split("-").map(Number);
  const base = Date.UTC(y!, m! - 1, d! + days);
  const dt = new Date(base);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function previousGameDate(gameDateStr: string): string {
  return addDays(gameDateStr, -1);
}

export interface Countdown {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
}

export function countdownTo(target: Date, now: Date = new Date()): Countdown {
  const totalSeconds = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    totalSeconds,
  };
}

export function formatCountdown(c: Countdown): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(c.hours)}:${pad(c.minutes)}:${pad(c.seconds)}`;
}

/**
 * Fecha del primer día de juego (ronda #1). Configurable con la variable
 * GAME_EPOCH; el número de ronda se deriva siempre de la fecha, así que es
 * secuencial y sin huecos aunque las rondas se programen desordenadas o se
 * consuma una reserva.
 */
export const DEFAULT_GAME_EPOCH = "2026-09-01";

export function daysBetween(fromDate: string, toDate: string): number {
  const [fy, fm, fd] = fromDate.split("-").map(Number);
  const [ty, tm, td] = toDate.split("-").map(Number);
  return Math.round((Date.UTC(ty!, tm! - 1, td!) - Date.UTC(fy!, fm! - 1, fd!)) / 86_400_000);
}

export function roundNumberFor(roundDate: string, epoch: string = DEFAULT_GAME_EPOCH): number {
  return daysBetween(epoch, roundDate) + 1;
}
